import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { createRaceOfficial } from '../lib/api';
import { EventDashboard } from './EventDashboard';
import { HeadJudgeDashboard } from './HeadJudgeDashboard';
import TimingDashboard from './TimingDashboard';

interface RaceOfficial {
  officialid: number;
  officialuuid: string | null;
  email: string;
  firstname: string | null;
  lastname: string | null;
  roleid: number | null;
  phonenumber: number | null;
  isactive: boolean;
  supervisorid: number | null;
  lastlogin: string | null;
  notes: string | null;
  judgestation: number | null;
  createdat: string;
  createdby: string | null;
}

export function RoleBasedDashboard() {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchUserRole() {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }

      try {
        // Query the raceofficial table to get the user's role
        console.log('Checking role for user email:', user.email);
        
        // Try exact match first
        let { data, error } = await supabase
          .from('raceofficial')
          .select('roleid, email, firstname, lastname')
          .eq('email', user.email)
          .eq('isactive', true)
          .single();

        // If no exact match, try case-insensitive search
        if (error && error.code === 'PGRST116') {
          console.log('No exact email match, trying case-insensitive search...');
          const { data: caseInsensitiveData, error: caseInsensitiveError } = await supabase
            .from('raceofficial')
            .select('roleid, email, firstname, lastname')
            .ilike('email', user.email)
            .eq('isactive', true)
            .single();
          
            if (caseInsensitiveError && caseInsensitiveError.code !== 'PGRST116') {
                console.error('Case-insensitive search failed:', caseInsensitiveError);
                error = caseInsensitiveError;
              } else if (caseInsensitiveError && caseInsensitiveError.code === 'PGRST116') {
                // User not found in case-insensitive search either
                console.log('User not found in case-insensitive search either');
                error = caseInsensitiveError;
              } else {
                data = caseInsensitiveData;
                error = null;
              }
        }

        // Debug: Let's also check what users exist in the table
        if (error) {
          console.log('Querying all users in raceofficial table for debugging...');
          const { data: allUsers, error: allUsersError } = await supabase
            .from('raceofficial')
            .select('roleid, email, firstname, lastname, isactive')
            .limit(10);
          
          if (!allUsersError && allUsers) {
            console.log('All users in raceofficial table:', allUsers);
          }
        }

        // Only exit for actual database errors, not "user not found"
        if (error && error.code !== 'PGRST116') {
            console.error('Error fetching user role:', error);
            setError('Failed to fetch user role');
            setIsLoading(false);
            return;
        }

        if (data) {
          console.log('Found user in raceofficial table:', data);
          console.log('User role ID:', data.roleid);
          setUserRole(data.roleid);
        } else {
          // User not found in raceofficial table - create them automatically
          console.log('User not found in raceofficial table, creating race official record...');
          
          try {
            setIsCreatingUser(true);
            
            // Extract first and last name from user metadata if available
            const firstname = user.user_metadata?.full_name?.split(' ')[0] || null;
            const lastname = user.user_metadata?.full_name?.split(' ').slice(1).join(' ') || null;
            
            console.log('About to call createRaceOfficial with:', {
                email: user.email,
                firstname: firstname,
                lastname: lastname
              });
              
              const createResponse = await createRaceOfficial({
                email: user.email,
                firstname: firstname,
                lastname: lastname
              });
              
              console.log('createRaceOfficial response:', createResponse);
            
            if (createResponse.success && createResponse.data) {
              console.log('Race official created successfully:', createResponse.data);
              // Set default role ID 4 (Head Judge) for new users
              setUserRole(createResponse.data.roleid || 4);
            } else {
              console.error('Failed to create race official:', createResponse.error);
              setError(`Failed to create user profile: ${createResponse.error}`);
            }
          } catch (createErr) {
            console.error('Error creating race official:', createErr);
            
            // If creation failed, check if user was created by another process
            console.log('Checking if user was created by another process...');
            
            // Add a small delay to allow for any database triggers or async processes
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            try {
              const { data: retryData, error: retryError } = await supabase
                .from('raceofficial')
                .select('roleid, email, firstname, lastname')
                .eq('email', user.email)
                .eq('isactive', true)
                .single();
              
              if (retryData && !retryError) {
                console.log('User was created by another process, using their role:', retryData);
                setUserRole(retryData.roleid);
              } else {
                console.error('User still not found after creation attempt:', retryError);
                setError('Failed to create user profile. Please try again.');
              }
            } catch (retryErr) {
              console.error('Error checking for user after creation failure:', retryErr);
              setError('Failed to create user profile. Please try again.');
            }
          } finally {
            setIsCreatingUser(false);
          }
        }
      } catch (err) {
        console.error('Error in fetchUserRole:', err);
        setError('An error occurred while fetching user role');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUserRole();
  }, [user?.email]);

  if (isLoading || isCreatingUser) {
    return (
      <div className="min-h-screen bg-hyrox-light flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hyrox-blue"></div>
          <p className="text-hyrox-text font-medium">
            {isCreatingUser ? 'Creating your profile...' : 'Checking access...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-hyrox-light flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg border border-red-200">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-500 mt-2">Please contact support if this issue persists.</p>
          <div className="mt-4 p-3 bg-gray-100 rounded">
            <p className="text-sm font-medium">Debug Info:</p>
            <p className="text-xs">User Email: {user?.email}</p>
            <p className="text-xs">User Role: {userRole}</p>
          </div>
        </div>
      </div>
    );
  }

  // Role-based routing logic
  console.log('Final userRole value:', userRole);
  console.log('Role comparison - userRole === 4:', userRole === 4);
  
  // If userRole is still null after all attempts, show error
  if (userRole === null) {
    return (
      <div className="min-h-screen bg-hyrox-light flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg border border-red-200">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Profile Creation Failed</h2>
          <p className="text-gray-600">We couldn't create your profile automatically. Please contact support.</p>
          <div className="mt-4 p-3 bg-gray-100 rounded">
            <p className="text-sm font-medium">Debug Info:</p>
            <p className="text-xs">User Email: {user?.email}</p>
            <p className="text-xs">User Role: {userRole}</p>
            <p className="text-xs">Error: {error || 'None'}</p>
          </div>
          <div className="mt-4">
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (userRole === 3) {
    // Race Director — default to Review Queue
    return <Navigate to="/review-penalty" replace />;
  }

  if (userRole === 4 || userRole === 5) {
    // Head / Station Judges — require station setup for this session, then hub
    const stationReady = sessionStorage.getItem('stationSetupComplete') === '1'
      && !!sessionStorage.getItem('currentLocation');
    if (!stationReady) {
      return <Navigate to="/set-station" replace />;
    }
    console.log('Routing to JudgeDashboard for Roles 4 and 5');
    return <HeadJudgeDashboard />;
  } else if (userRole === 6) {
    console.log('Routing to Timing Panel Dashboard');
    return <TimingDashboard />;
  }
   else {
    // All other roles (including null) go to EventDashboard
    console.log('Routing to EventDashboard');
    return <EventDashboard />;
  }
}
